import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { api } from '@/services/api';
import { serverCode, serverMessage, serverStatus } from '@/lib/serverMessage';
import { useAuth } from '@/contexts/AuthContext';
import { AuthShell } from '@/components/auth/AuthShell';
import { MonoLabel } from '@/components/editorial';

// The server's own sentence, or null when the failure carried none.
function serverMessageOrNull(err: unknown): string | null {
  const m = serverMessage(err, '');
  return m || null;
}

type VerifyState = 'idle' | 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  // #2109 R3 L8 (#2133): the words are `forms:verifyEmail.*`. The server's
  // failure sentence is English, so only an English reader sees it (R3-B11).
  const { t, i18n } = useTranslation('forms');
  const { t: tAuth } = useTranslation('auth');
  const english = i18n.language === 'en' || i18n.language.startsWith('en-');
  const token = searchParams.get('token');

  const [state, setState] = useState<VerifyState>(token ? 'loading' : 'idle');
  // The server's sentence; `null` when the failure carried none (a non-Error
  // throw), which reads as `forms:verifyEmail.expired`.
  const [errorMessage, setErrorMessage] = useState<string | null>('');
  // The link was already spent, so the address is most likely confirmed (#2695).
  const [linkUsed, setLinkUsed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState<'rateLimited' | 'failed' | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const run = async () => {
      try {
        await api.auth.verifyEmail(token);
        if (!cancelled) setState('success');
      } catch (err: unknown) {
        if (!cancelled) {
          // Held as the raw sentence; the render picks the words, so a
          // language switch after the failure still re-renders.
          const message = serverMessageOrNull(err);
          setLinkUsed(serverCode(err) === 'VERIFY_LINK_USED_OR_EXPIRED');
          setErrorMessage(message);
          setState('error');
        }
      }
    };

    run();
    return () => { cancelled = true; };
  }, [token]);

  const handleResend = async () => {
    setResending(true);
    setResendError(null);
    try {
      await api.auth.sendEmailVerification();
      setResent(true);
    } catch (err: unknown) {
      // #2310 follow-up: resending is capped per account (429). Say so, and
      // say something on any other failure too, so the re-enabled button is
      // never the only sign the email did not go.
      setResendError(serverStatus(err) === 429 ? 'rateLimited' : 'failed');
    } finally {
      setResending(false);
    }
  };

  // Held as a kind, not a sentence, so a language switch re-renders it.
  const resendErrorLine = resendError && (
    <p
      role="status"
      data-testid="verify-resend-error"
      className="mt-3 text-body text-status-danger-fg max-w-[400px]"
    >
      {resendError === 'rateLimited' ? t('verifyEmail.resendRateLimited') : t('verifyEmail.resendFailed')}
    </p>
  );

  return (
    <AuthShell documentLabel={t('verifyEmail.documentLabel')}>
      <div className="mb-5">
        <MonoLabel>{t('applyChrome.applyToTrf')}</MonoLabel>
      </div>

      {state === 'loading' && (
        <>
          <h1
            className="text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4 font-serif"
          >
            {t('verifyEmail.confirmingTitle')}
          </h1>
          <div className="flex items-center gap-3 text-stevens-ink-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-body">{t('verifyEmail.justAMoment')}</span>
          </div>
        </>
      )}

      {state === 'idle' && (
        <>
          <h1
            className="text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4 font-serif"
          >
            {t('verifyEmail.checkInboxTitle')}
          </h1>
          <p className="text-lede leading-relaxed text-stevens-ink-2 mb-6 max-w-[400px]">
            {t('verifyEmail.checkInboxLede')}
          </p>
          {isAuthenticated && (
            <button
              onClick={handleResend}
              disabled={resending || resent}
              className="text-body text-stevens-indigo hover:underline disabled:opacity-60"
            >
              {resent ? t('verifyEmail.resent') : resending ? t('verifyEmail.sending') : t('verifyEmail.resend')}
            </button>
          )}
          {isAuthenticated && resendErrorLine}
        </>
      )}

      {state === 'success' && (
        <>
          <div className="mb-4 flex items-center gap-2 text-status-ok-fg">
            <CheckCircle className="h-5 w-5" />
            <MonoLabel>{t('verifyEmail.confirmedLabel')}</MonoLabel>
          </div>
          <h1
            className="text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4 font-serif"
          >
            {t('verifyEmail.confirmedTitle')}
          </h1>
          <p className="text-lede leading-relaxed text-stevens-ink-2 mb-8 max-w-[400px]">
            {t('verifyEmail.confirmedLede')}
          </p>
          <Link
            to="/apply/wizard"
            className="inline-flex items-center justify-center rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2"
          >
            {t('verifyEmail.continue')}
          </Link>
        </>
      )}

      {state === 'error' && (
        <>
          <div className="mb-4 flex items-center gap-2 text-status-danger-fg">
            <XCircle className="h-5 w-5" />
            <MonoLabel>{t('verifyEmail.failedLabel')}</MonoLabel>
          </div>
          <h1
            className="text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4 font-serif"
          >
            {t('verifyEmail.failedTitle')}
          </h1>
          <p className="text-lede leading-relaxed text-stevens-ink-2 mb-6 max-w-[400px]">
            {linkUsed
              ? t('verifyEmail.failedFallback')
              : !english
              ? t('verifyEmail.failedFallback')
              : errorMessage === null
                ? t('verifyEmail.expired')
                : errorMessage || t('verifyEmail.failedFallback')}
          </p>
          {/* #2695: a used link is a dead end without a way forward; most often the email is already confirmed. */}
          {linkUsed && (
            <Link
              to="/login"
              data-testid="verify-used-sign-in"
              className="mb-4 inline-flex items-center justify-center rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2"
            >
              {tAuth('login.signIn')}
            </Link>
          )}
          {isAuthenticated && (
            <button
              onClick={handleResend}
              disabled={resending || resent}
              className="text-body text-stevens-indigo hover:underline disabled:opacity-60"
            >
              {resent ? t('verifyEmail.resent') : resending ? t('verifyEmail.sending') : t('verifyEmail.sendNew')}
            </button>
          )}
          {isAuthenticated && resendErrorLine}
        </>
      )}
    </AuthShell>
  );
}
